import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';

import { Field, Formik, Form } from 'formik';
import * as yup from 'yup';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { BOOKING_PROCESS } from '@common/network';
import { notificationType } from '@components/Notifications/Constants';
import SelectInput from '@components/SelectInput/SelectInput';
import TextInput from '@components/TextInput/TextInput';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { patchByPathAndData } from '@services/BaseApi';

const initialValues = {
  cancellationReasonId: 6,
  cancellationComment: ''
};

const MAX_CHARACTER_LIMIT = 255;

const validateCancellationForm = () =>
  yup.object().shape({
    cancellationComment: yup
      .string()
      .max(MAX_CHARACTER_LIMIT, Localize.get('Booking.characterLimit'))
      .notRequired(),
    cancellationReasonId: yup.number().required(Localize.get('Validation.requiredField'))
  });

const CancellationReasonTypes = {
  VACATION: 5,
  ILLNESS: 6,
  ILLNESS_OF_CHILD: 7,
  OVERLAPPING_SCHEDULE: 8,
  NO_LONGER_NEEDED: 14,
  NO_TIME: 15,
  PARTICIPANT_CANCEL: 3,
  SCHEDULE_CONFLICT: 22,
  NO_INTEREST: 23,
  OTHER_REASONS: 24
};

export const allowedCancellationReasons = [
  CancellationReasonTypes.VACATION,
  CancellationReasonTypes.ILLNESS,
  CancellationReasonTypes.ILLNESS_OF_CHILD,
  CancellationReasonTypes.OVERLAPPING_SCHEDULE,
  CancellationReasonTypes.NO_LONGER_NEEDED,
  CancellationReasonTypes.NO_TIME,
  CancellationReasonTypes.PARTICIPANT_CANCEL,
  CancellationReasonTypes.NO_INTEREST,
  CancellationReasonTypes.OTHER_REASONS,
  CancellationReasonTypes.SCHEDULE_CONFLICT
];

const FormSubmitter = ({
  onSubmit,
  submitCancellationForm,
  setSubmitCancellationForm,
  values,
  isValid
}) => {
  useEffect(() => {
    if (submitCancellationForm && isValid) {
      onSubmit(values);
    } else {
      setSubmitCancellationForm(false);
    }
  }, [submitCancellationForm]);

  return null;
};

const CreateCancellationForm = ({
  cancelBooking,
  setCancelBooking,
  cancellationReasons,
  uuid,
  setCancellationSuccess
}) => {
  const [submitCancellationForm, setSubmitCancellationForm] = useState(false);
  const [contextData, setContextData] = useGlobalContext();

  const handleDeclineRegistration = async (values) => {
    await patchByPathAndData({
      path: BOOKING_PROCESS.DECLINE_INVITATION,
      pathVariables: { uuid },
      data: values,
      temApi: true
    })
      .then((res) => {
        const { status } = res;
        const { message } = res.response?.data || {};
        if (status !== 200) {
          setContextData({
            ...contextData,
            open: true,
            message: message || Localize.get('Tables.Error'),
            severity: notificationType.error
          });
          setSubmitCancellationForm(false);
          return;
        }
        setCancellationSuccess(true);
        setCancelBooking(false);
      })
      .catch((error) => {
        console.error('error', error);
      })
      .finally(() => {});
  };

  return (
    <>
      <Dialog
        open={cancelBooking}
        onClose={() => setCancelBooking(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        fullWidth={true}
        maxWidth='sm'
        sx={{
          height: '50rem',
          margin: 'auto',
          overflowY: 'scroll',
          overflowX: 'hidden',
          '& .MuiDialog-paper': {
            backgroundColor: (theme) => `${theme.palette.booking.background}!important`
          }
        }}
      >
        <DialogTitle id='alert-dialog-title'>
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}
          >
            {Localize.get('Booking.declineInvite')}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 4, fontSize: '1rem', minHeight: '10rem' }}>
          {
            <Formik
              validateOnMount
              initialValues={initialValues}
              validationSchema={validateCancellationForm}
            >
              {({ values, isValid }) => {
                return (
                  <Form>
                    <Grid container sx={{ padding: '0.5rem' }}>
                      <Grid item xs={12} sx={{ mb: 1 }}>
                        <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                          <Field
                            name={'cancellationReasonId'}
                            label={Localize.get('Booking.cancellationReason')}
                            component={SelectInput}
                            options={cancellationReasons}
                            backgroundField={true}
                            required
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl required fullWidth size='small'>
                          <Field
                            name={'cancellationComment'}
                            label={Localize.get('Booking.cancellationComment')}
                            component={TextInput}
                            rows={3}
                            multiline={true}
                            backgroundField={true}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <FormSubmitter
                      values={values}
                      onSubmit={handleDeclineRegistration}
                      submitCancellationForm={submitCancellationForm}
                      setSubmitCancellationForm={setSubmitCancellationForm}
                      isValid={isValid}
                    />
                  </Form>
                );
              }}
            </Formik>
          }
        </DialogContent>
        <DialogActions sx={{ backgroundColor: (theme) => theme.palette.primary.background }}>
          <Button
            sx={{ minWidth: '8rem' }}
            onClick={() => setCancelBooking(false)}
            variant='outlined'
          >
            {Localize.get('App.cancel')}
          </Button>
          <Button
            sx={(theme) => ({ color: theme.palette.common.white })}
            variant='contained'
            onClick={() => setSubmitCancellationForm(true)}
          >
            {Localize.get('Booking.declineInvite')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default CreateCancellationForm;
